import * as THREE from 'three';

/**
 * Shader pre-warm (docs/PERF.md "Frame pacing and hitches"). three.js compiles a material's program the first
 * time it is drawn, and that draw blocks until the driver has compiled and linked it: 50–250 ms per program
 * on ANGLE / D3D, and the first frame of the city waited on ~60 of them at once. `renderer.compileAsync`
 * hands the same programs to the driver up front; where the browser has `KHR_parallel_shader_compile` it
 * compiles them on its own threads while the page keeps running, elsewhere the main thread does the work
 * here instead of on a frame the player sees.
 *
 * `root` need not be in `scene`: its programs are built for `scene`'s lights, fog and shadows, i.e. as they
 * will be once it is added. Resolves after `capMs` at the latest (a lost context never reports ready).
 */
export function warmPrograms(renderer: THREE.WebGLRenderer, root: THREE.Object3D, camera: THREE.Camera, scene: THREE.Scene | null = null, capMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, capMs);
    try {
      renderer.compileAsync(root, camera, scene ?? undefined).then(done, done);
    } catch {
      done();
    }
  });
}

/**
 * Objects the boot pre-warm compiles along with the scene (renderSystem warmTier), for programs that are not in
 * the scene yet but will be a few seconds in: the detailed cars' textured body, whose models now land after the
 * loading screen on a slow line, compiled on the frame they appeared (a 0.5–0.75 s stall in the first seconds).
 * They are never drawn; keep them alive, or three drops the program again.
 */
const warmExtras = new THREE.Group();

export function warmAlongside(o: THREE.Object3D): void {
  warmExtras.add(o);
}

/** The objects to pre-warm along with the scene, or null. */
export function warmExtrasRoot(): THREE.Object3D | null {
  return warmExtras.children.length ? warmExtras : null;
}

/** Files three.js' loaders (glTF, KTX2, textures: all on the default manager) are still fetching or decoding. */
let pendingLoads = 0;
let tracking = false;

/** Count the default loading manager's open items from now on (main.ts, before the game is built). */
export function trackLoads(): void {
  if (tracking) return;
  tracking = true;
  const m = THREE.DefaultLoadingManager;
  const start = m.itemStart.bind(m);
  const end = m.itemEnd.bind(m);
  m.itemStart = (url: string) => {
    pendingLoads++;
    start(url);
  };
  m.itemEnd = (url: string) => {
    pendingLoads = Math.max(0, pendingLoads - 1);
    end(url);
  };
}

/** Loads the first view cannot do without (the city's textures), still in flight (needForFirstView). */
let firstViewPending = 0;

/**
 * Hold the loading screen until `p` settles: a texture or sheet the first view shows (the facade atlas, the
 * ground, the skyline, the sign plates), so the player never sees the placeholder colours swap to the real
 * thing. Models (cars, the crowd, the player) are not first-view loads: they keep their procedural stand-ins
 * until they land, and settleFirstFrames waits for them only briefly.
 */
export function needForFirstView(p: Promise<unknown>): void {
  firstViewPending++;
  const done = (): void => {
    firstViewPending = Math.max(0, firstViewPending - 1);
    checkHold();
  };
  p.then(done, done);
}

/**
 * The boot's download hold (docs/PERF.md "Boot: readiness instead of a fixed wait"): on a phone's bandwidth the
 * models requested once the systems are built (the detailed cars, the crowd, the player, the weapons, the effects
 * atlas: ~10 MB) took their share from the facade atlas and the ground, which the first view needs. Those loads
 * wait for afterFirstView(); it resolves once the city has registered its first-view loads (sealFirstView) and they
 * have landed, or at the cap. Without holdDownloadsForFirstView (tests, headless) it resolves at once.
 */
let hold: { sealed: boolean; promise: Promise<void>; release: () => void } | null = null;
/** When the city sealed its first-view loads, and how long after that they all had landed (-1: not yet). */
let sealedAt = -1;
let lagMs = -1;

/** main.ts, before the game is built. */
export function holdDownloadsForFirstView(capMs = 15000): void {
  if (hold) return;
  let release!: () => void;
  const promise = new Promise<void>((r) => (release = r));
  const timer = setTimeout(() => h.release(), capMs);
  const h = {
    sealed: false,
    promise,
    release: () => {
      clearTimeout(timer);
      h.sealed = true;
      release();
    },
  };
  hold = h;
}

/** The world system, once the city view has registered its first-view loads. */
export function sealFirstView(): void {
  sealedAt = performance.now();
  performance.mark?.('bh:city');
  if (!hold) return;
  hold.sealed = true;
  checkHold();
}

/**
 * How long settleFirstFrames waits for the model loads once the first view is in. The first-view textures were
 * requested during the world's build: how far they landed behind the CPU (the time from sealFirstView to the last
 * of them) says what the network is like. Just behind or ahead of it (wifi, a fast line), the models, about twice
 * those bytes, are in a moment later: wait up to 1.5 s for them, so they do not pop in (and compile) right after
 * the screen lifts. Seconds behind (4G), they would take many seconds more: don't wait.
 */
export function modelGraceMs(): number {
  if (lagMs < 0) return 500;
  return Math.max(0, 1500 - 1.5 * lagMs);
}

/** Release the hold now (the loading screen has gone). */
export function releaseDownloadHold(): void {
  hold?.release();
}

/** Resolves when the boot's held downloads may start (see holdDownloadsForFirstView). */
export function afterFirstView(): Promise<void> {
  return hold ? hold.promise : Promise.resolve();
}

function checkHold(): void {
  if (sealedAt >= 0 && lagMs < 0 && firstViewPending === 0) {
    lagMs = performance.now() - sealedAt;
    performance.mark?.('bh:firstview');
  }
  if (hold?.sealed && firstViewPending === 0) hold.release();
}

/** Whether every first-view load registered so far has landed. */
export function firstViewReady(): boolean {
  return firstViewPending === 0;
}

export interface SettleOptions {
  /** Frames to run at least. */
  minFrames?: number;
  /** After the first view is in, how long other loads (models) may still hold the screen (default modelGraceMs()). */
  graceMs?: number;
  /** Give up waiting for the first-view loads after this long (a very slow network shows what it has). */
  hardCapMs?: number;
}

/**
 * Wait, while the game runs under the loading screen, until its first view is right (docs/PERF.md "Boot:
 * readiness instead of a fixed wait"): every first-view load (needForFirstView) landed, then the model loads
 * started so far finished or their grace passed (modelGraceMs), then two frames in a row without a new program
 * (what a load adds is drawn, compiled and uploaded the frame after it lands), and at least `minFrames` frames.
 * On a slow network the models keep loading in the background; on a fast one they are in before the screen
 * lifts, as before (each would otherwise cost a 0.2–0.7 s compile stall right after it). Gives up `capMs` after the
 * first view was ready, or `hardCapMs` after the start.
 */
export function settleFirstFrames(renderer: THREE.WebGLRenderer, capMs = 4000, opts: SettleOptions = {}): Promise<void> {
  const minFrames = opts.minFrames ?? 3;
  const hardCapMs = opts.hardCapMs ?? 15000;
  return new Promise((resolve) => {
    const t0 = performance.now();
    let readyAt = -1;
    let frames = 0;
    let quiet = 0;
    let programs = renderer.info.programs?.length ?? 0;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      resolve();
    };
    const tick = (): void => {
      if (finished) return;
      frames++;
      const now = performance.now();
      if (readyAt < 0 && firstViewPending === 0) {
        readyAt = now;
        checkHold();
      }
      const settled = readyAt >= 0 && (pendingLoads === 0 || now - readyAt > (opts.graceMs ?? modelGraceMs()));
      const count = renderer.info.programs?.length ?? 0;
      quiet = count === programs && settled ? quiet + 1 : 0;
      programs = count;
      if ((frames >= minFrames && quiet >= 2) || (readyAt >= 0 && now - readyAt > capMs) || now - t0 > hardCapMs) finish();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    // A hidden tab runs no frames.
    setTimeout(finish, hardCapMs + 500);
  });
}

/** Whether this browser compiles programs off the main thread (`__bh.renderBudget().parallelCompile`). */
export function parallelCompile(renderer: THREE.WebGLRenderer): boolean {
  return renderer.extensions.has('KHR_parallel_shader_compile');
}
